/**
 * Background Sync Engine
 * 
 * Fetches data from Cloud APIs and populates the local SQLite cache.
 * Also replays queued write mutations when back online.
 * Runs on app load and periodically in the background.
 */

'use client';

import { 
  cacheDrugs, 
  cachePatientsToDB, 
  cacheQueueToDB, 
  cacheSettingsToDB,
  cachePrescriptionsToDB,
  getPendingMutations,
  updateMutationStatus,
  clearCompletedMutations,
  getOfflineDB
} from './db';
import { LAST_SYNC_KEY, etagKey, readLocal, stampAge, tableKey, writeLocal } from './sync-state';

const SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes
const SYNC_LOCK = 'clirxos-offline-sync';
let syncTimer: ReturnType<typeof setTimeout> | null = null;
let initialTimer: ReturnType<typeof setTimeout> | null = null;
let stopListeners: (() => void) | null = null;

/** The patients endpoint hands out its list this many at a time (its own ceiling for one request). */
const PATIENT_PAGE = 5000;
/** A list is read to its end, but never past this many pages: the server stops honouring `offset` at 100,000 rows. */
const MAX_PAGES = 20;
/** The URL of the page after this one (`got` rows are held so far), or null when this was the last. */
type NextPage = (page: { hasMore?: boolean } | undefined, got: number) => string | null;

/** Rows the local cache holds for a table, from the bookkeeping row written when it was last filled. */
async function cachedCount(tableName: string): Promise<number> {
  try {
    const db = await getOfflineDB();
    if (!db) return 0;
    const rows = await db.query(`SELECT record_count FROM sync_meta WHERE table_name = ?`, [tableName]);
    return Number(rows[0]?.record_count ?? 0);
  } catch { return 0; }
}

/** The table is up to date as of now - keeps autoPurgeStaleData() from judging a table nobody changed "stale". */
async function touchSyncMeta(tableName: string): Promise<void> {
  try {
    const db = await getOfflineDB();
    await db?.execute(`UPDATE sync_meta SET last_synced_at = ? WHERE table_name = ?`, [Date.now(), tableName]);
  } catch { /* the next full fill writes it */ }
}

/** How long a table may go without being fetched again by the background timer. */
const TABLE_EVERY_MS: Record<string, number> = {
  prescriptions: 30 * 60 * 1000, // the heaviest list, and read only as the prescriptions page's fallback when the network is gone
};
const tableDue = (tableName: string) =>
  stampAge(tableKey(tableName)) >= (TABLE_EVERY_MS[tableName] ?? 0) * 0.8;

/**
 * Sync a specific table from the API to local SQLite.
 *
 * An endpoint that can say which version of its list it holds (an ETag, see lib/http-cache.ts) lets
 * this skip the work entirely when the version is the one already stored: no JSON parse of
 * megabytes, no thousands of INSERTs. The browser has already answered the request with a 304, so
 * the server did not build the list either. The version is trusted only while the local table still
 * has its rows (a purge empties it without touching localStorage).
 *
 * A list the server hands out in pages (`nextPage` names the page after the one just read) is read to its end. The
 * version that counts is the first page's: the server derives every page's from the same two facts (how many rows,
 * when the newest changed), so if the first page is the one held, so are the rest. It is stored only when every
 * page was read; a page that fails leaves what was read in the cache, and the list is asked for again next cycle.
 */
async function syncTable(
  tableName: string,
  apiUrl: string,
  cacheFunc: (data: any) => Promise<void>,
  extractData: (response: any) => any[],
  nextPage?: NextPage
): Promise<number | null> {
  try {
    const res = await fetch(apiUrl);
    if (!res.ok) return null;

    const etag = res.headers.get('etag');
    if (etag && etag === readLocal(etagKey(tableName))) {
      const held = await cachedCount(tableName);
      if (held > 0) {
        res.body?.cancel().catch(() => {});
        await touchSyncMeta(tableName);
        return held;
      }
    }

    const data = await res.json();
    let records = extractData(data);

    let complete = true;
    let url = nextPage?.(data, records.length) ?? null;
    for (let pages = 1; url && pages < MAX_PAGES; pages++) {
      try {
        const more = await fetch(url);
        if (!more.ok) throw new Error(`HTTP ${more.status}`);
        const page = await more.json();
        const rows = extractData(page);
        records = records.concat(rows);
        url = rows.length > 0 ? nextPage?.(page, records.length) ?? null : null; // a page with nothing in it is the end, whatever it claims
      } catch (error) {
        console.warn(`[Sync] ${tableName}: a page could not be read, keeping the ${records.length} rows read so far`, error);
        complete = false;
        break;
      }
    }
    if (url && complete) console.warn(`[Sync] ${tableName}: stopped after ${MAX_PAGES} pages (${records.length} rows)`);

    if (records.length > 0) {
      await cacheFunc(records);
      if (etag && complete) writeLocal(etagKey(tableName), etag);
    }

    return records.length;
  } catch (error) {
    // Network error — skip silently (we're probably offline)
    console.log(`[Sync] Skipped ${tableName} (network unavailable)`);
    return null;
  }
}

/**
 * Replay queued write mutations
 * Returns { replayed: number, failed: number }
 */
export async function replayWriteQueue(): Promise<{ replayed: number; failed: number }> {
  const mutations = await getPendingMutations();
  if (mutations.length === 0) return { replayed: 0, failed: 0 };

  console.log(`[WriteQueue] Replaying ${mutations.length} pending mutations...`);
  let replayed = 0;
  let failed = 0;

  for (const m of mutations) {
    try {
      await updateMutationStatus(m.id, 'syncing');

      const res = await fetch(m.endpoint, {
        method: m.method,
        headers: { 'Content-Type': 'application/json' },
        body: m.body,
      });

      if (res.ok || res.status === 201) {
        await updateMutationStatus(m.id, 'done');
        replayed++;
        console.log(`[WriteQueue] ✅ Replayed: ${m.method} ${m.endpoint}`);
      } else {
        const errorText = await res.text().catch(() => `HTTP ${res.status}`);
        if (res.status === 401) {
          // Nobody is signed in: the data is not bad, it has not been sent. It stays in the queue for the next sign-in.
          await updateMutationStatus(m.id, 'failed', errorText);
          failed++;
        } else if (res.status >= 400 && res.status < 500) {
          // Don't retry other 4xx errors (client errors — data is bad)
          await updateMutationStatus(m.id, 'done', `Client error: ${errorText}`);
          console.warn(`[WriteQueue] ⚠️ Skipped (client error): ${m.method} ${m.endpoint}`);
        } else {
          await updateMutationStatus(m.id, 'failed', errorText);
          failed++;
        }
      }
    } catch (error) {
      await updateMutationStatus(m.id, 'failed', String(error));
      failed++;
      console.error(`[WriteQueue] ❌ Failed: ${m.method} ${m.endpoint}`, error);
    }
  }

  // Clean up old completed mutations
  await clearCompletedMutations();

  console.log(`[WriteQueue] Replay complete: ${replayed} replayed, ${failed} failed`);
  return { replayed, failed };
}

/**
 * Run full sync of all tables
 */
export async function runFullSync(opts: { onlyDue?: boolean } = {}): Promise<{ synced: Record<string, number>; success: boolean }> {
  console.log('[Sync] Starting full sync...');
  
  const synced: Record<string, number> = {};
  // The timer asks for what is due (some lists are fetched less often than every cycle); "sync now" asks for everything.
  const run = async (tableName: string, url: string, cacheFunc: (data: any) => Promise<void>, extract: (response: any) => any[], nextPage?: NextPage) => {
    if (opts.onlyDue && !tableDue(tableName)) return;
    const n = await syncTable(tableName, url, cacheFunc, extract, nextPage);
    synced[tableName] = n ?? 0;
    if (n !== null) writeLocal(tableKey(tableName), String(Date.now())); // a failed fetch is tried again next cycle
  };
  
  try {
    // 0. Replay write queue FIRST (before reading new data)
    if (navigator.onLine) {
      const { replayed, failed } = await replayWriteQueue();
      if (replayed > 0 || failed > 0) {
        synced.writeQueue = replayed;
      }
    }

    // 1. Sync drugs catalog. ?view=cache is the compact form (no substitutes, packagings or categories, which the cache
    //    does not keep) and covers every active drug: the full list is capped at 500, so the offline cache held only the
    //    first 500 by name.
    await run('drugs', '/api/drugs?view=cache', cacheDrugs, (data) => (Array.isArray(data) ? data : []));

    // 2. Sync patients: newest-changed first, PATIENT_PAGE at a time. One request used to be the whole list, and a clinic
    //    with more patients than that (production had 5,410) never cached its oldest ones.
    await run(
      'patients',
      `/api/patients?limit=${PATIENT_PAGE}`,
      cachePatientsToDB,
      (data) => data?.patients || [],
      (page, got) => (page?.hasMore === true ? `/api/patients?limit=${PATIENT_PAGE}&offset=${got}` : null)
    );

    // 3. Sync queue
    await run('queue', '/api/queue', cacheQueueToDB, (data) => data?.queue || []);

    // 4. Sync settings
    await run('settings', '/api/settings', async (settings) => { await cacheSettingsToDB(settings); }, (data) => data?.settings || []);

    // 5. Sync prescriptions (last 200 — latest first)
    await run(
      'prescriptions',
      '/api/prescriptions?page=1&pageSize=200',
      async (records) => { await cachePrescriptionsToDB(records); },
      (data) => {
        const items = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        return items.map((item: any) => ({
          id: item.id || item.rowKey || '',
          sotoa: item.sotoa,
          ngay: item.ngay,
          mabs: item.mabs,
          sonv: item.sonv,
          loidan: item.loidan,
          taikham: item.taikham,
          doctorName: item.doctorName,
          patientId: item.patientId,
          patientName: item.patientName,
          totalEstimate: item.totalEstimate,
          items: item.items || [],
        }));
      }
    );
    
    console.log('[Sync] Complete:', synced);
    return { synced, success: true };
  } catch (error) {
    console.error('[Sync] Failed:', error);
    return { synced, success: false };
  }
}

/**
 * Run a full sync when one is due, and only in ONE tab of this browser at a time.
 *
 * Every open tab used to run its own sync on its own five-minute timer: five lists, 5 MB and a quarter of
 * a second of the server's single thread each, and the pending-write replay twice over when two tabs came
 * back online together. Now a tab syncs only if it is visible, no other tab holds the lock, and nobody
 * synced in the last interval; a hidden tab catches up the moment it is shown again.
 *
 * `force` is for coming back online: the write queue must go out promptly, due or not.
 */
export async function runSyncIfDue(force = false): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  if (!force && document.visibilityState === 'hidden') return;
  const dueIn = () => SYNC_INTERVAL * 0.8 - stampAge(LAST_SYNC_KEY);
  if (!force && dueIn() > 0) return;

  const work = async () => {
    // somebody may have finished while this waited for the lock
    if (!force && dueIn() > 0) return;
    await runFullSync({ onlyDue: !force });
    writeLocal(LAST_SYNC_KEY, String(Date.now()));
  };
  if (navigator.locks?.request) {
    await navigator.locks.request(SYNC_LOCK, { ifAvailable: true }, async (lock) => { if (lock) await work(); });
  } else {
    await work();
  }
}

/** Five minutes, give or take a fifth, so tabs and machines opened together do not keep to one beat. */
const nextDelay = () => SYNC_INTERVAL * (0.8 + Math.random() * 0.4);

/**
 * Start periodic background sync
 */
export function startBackgroundSync(): void {
  if (syncTimer || initialTimer || stopListeners) return; // Already running

  // First sync a few seconds after load (let the app paint and its own requests finish), at a random moment
  // so a room of computers switched on together does not ask for the same lists in the same second.
  initialTimer = setTimeout(() => {
    initialTimer = null;
    runSyncIfDue();
  }, 4000 + Math.random() * 11000);

  const tick = () => {
    syncTimer = setTimeout(() => { runSyncIfDue(); tick(); }, nextDelay());
  };
  tick();

  // A tab that was hidden (no sync while hidden) catches up when it is shown
  const onVisible = () => { if (document.visibilityState === 'visible') runSyncIfDue(); };
  // Sync when coming back online: the pending writes first, so not subject to "due"
  const onOnline = () => {
    console.log('[Sync] Network restored, syncing...');
    setTimeout(() => runSyncIfDue(true), Math.random() * 3000);
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('online', onOnline);
  stopListeners = () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('online', onOnline);
  };

  console.log(`[Sync] Background sync started (about every ${SYNC_INTERVAL / 1000}s, visible tab, one tab at a time)`);
}

/**
 * Stop background sync
 */
export function stopBackgroundSync(): void {
  if (syncTimer) { clearTimeout(syncTimer); syncTimer = null; }
  if (initialTimer) { clearTimeout(initialTimer); initialTimer = null; }
  if (stopListeners) { stopListeners(); stopListeners = null; console.log('[Sync] Background sync stopped'); }
}
